import { TriangleAlert } from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";

import { AutomationsBoard } from "@/features/automations/board/AutomationsBoard";
import {
  exploreGroupsForCase,
  starterRunMessage
} from "@/features/automations/board/caseExploreStarters";
import {
  BoardEntry,
  type BoardSource
} from "@/features/automations/board/BoardEntry";
import { cn } from "@/lib/utils";

import { useCaseIde } from "../../context/CaseIdeContext";
import { useCaseVisa } from "../../useCaseVisa";
import { WORKFLOW_GROUPS, WORKFLOWS } from "../catalog";
import { useWorkflows } from "../context/WorkflowsContext";
import { useCaseAutomations } from "../hooks/useCaseAutomations";
import { AddFilesFirst } from "./AddFilesFirst";
import { ASK_EVERY_COPY } from "./RunAllButton";
import { WorkflowGroup } from "./WorkflowGroup";
import { WorkflowHeader } from "./WorkflowHeader";

type Props = {
  className?: string;
};

/**
 * Case Automations: two ways in, your firm's automations first and case
 * preparation beside it. An entry point that names a side (Let CaseBlink
 * Prepare opens case preparation) lands there. With no intent it opens on
 * case preparation, every time the tab mounts (returning to it included); a
 * choice made here lasts while it stays open. Case preparation is the nine
 * workflows, plus the optional Draft Exhibit List, in their five groups under
 * a title-only header with its run
 * actions (see `WorkflowHeader`); each group is one container of cards, and a vertical stepper runs
 * down the left of the groups (see `WorkflowGroup`, `WorkflowStepper`).
 */
export const WorkflowLaunchpad: FC<Props> = ({ className }) => {
  const w = useWorkflows();
  const {
    requestRunAutomation,
    requestSend,
    workflowsEntry,
    clearWorkflowsEntry
  } = useCaseIde();
  const automations = useCaseAutomations();
  const caseVisa = useCaseVisa();
  const exploreGroups = useMemo(
    () => exploreGroupsForCase(caseVisa || null),
    [caseVisa]
  );
  // An entry point's explicit intent (see `useOpenWorkflowsTab`) wins over the
  // default, whether it arrived before this mounted or while it was open.
  const [chosen, setChosen] = useState<BoardSource | null>(
    workflowsEntry?.source ?? null
  );
  useEffect(() => {
    if (!workflowsEntry) return;
    setChosen(workflowsEntry.source);
    clearWorkflowsEntry(workflowsEntry.nonce);
  }, [workflowsEntry, clearWorkflowsEntry]);
  const source: BoardSource = chosen ?? "caseblink";
  const byId = new Map(w.views.map((v) => [v.id, v]));
  // Sections in order, with the number of each one's first step.
  let count = 0;
  const sections = WORKFLOW_GROUPS.map((group, gi) => {
    const defs = WORKFLOWS.filter((d) => d.group === group.id);
    const start = count + 1;
    count += defs.length;
    return {
      group,
      defs,
      start,
      first: gi === 0,
      last: gi === WORKFLOW_GROUPS.length - 1
    };
  });

  return (
    <section
      className={cn(
        "workflow-launchpad @container flex w-full flex-col gap-5",
        // The card's padding and title row set where the stepper's nodes sit;
        // a group's padding, the gap between groups and its header's gap set
        // how far the line runs from one group's last node to the next's.
        "[--wf-pad:0.75rem] [--wf-row:max(1.5rem,var(--text-base--line-height))] [--wf-center:calc(var(--wf-pad)+var(--wf-row)/2)]",
        "[--wf-gpad:0.5rem] [--wf-ggap:0.75rem] [--wf-hgap:0.5rem]",
        "[--wf-bridge:calc(-2*var(--wf-gpad)-var(--wf-ggap))] [--wf-hbridge:calc(-1*var(--wf-hgap))]",
        className
      )}
      data-testid="workflow-launchpad"
      aria-label="Case Automations"
    >
      <BoardEntry
        value={source}
        onChange={setChosen}
        automations={automations.length}
      />

      {source === "firm" ? (
        <AutomationsBoard
          skills={automations}
          exploreGroups={exploreGroups}
          onRun={(skill) => requestRunAutomation(skill.id)}
          onRunStarter={(starter) => requestSend(starterRunMessage(starter))}
        />
      ) : (
        <>
          <WorkflowHeader />

          <AddFilesFirst />

          {w.settings.askEveryWrite ? (
            <p className="flex items-start gap-1.5 text-sm text-warn">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              {ASK_EVERY_COPY}
            </p>
          ) : null}

          <div className="flex flex-col gap-(--wf-ggap)">
            {sections.map((s) => (
              <WorkflowGroup key={s.group.id} views={byId} {...s} />
            ))}
          </div>
        </>
      )}
    </section>
  );
};
